import React, { useState, useEffect, useMemo } from 'react'
import { Layout } from '../../components/Layout/Layout'
import { Modal } from '../../components/Modal/Modal'
import { PartnershipForm } from './PartnershipForm'
import api from '../../api/axios'
import Icon from '../../design/icons'
import { exportRowsAsCsv } from '../../utils/csv'
import { T, useContent } from '../../content/ContentProvider'

export function Partnerships() {
  const { t } = useContent()
  const [partnerships, setPartnerships] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selected, setSelected] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [status, setStatus] = useState('all')
  const [search, setSearch] = useState('')

  useEffect(() => {
    fetchPartnerships()
  }, [])

  const fetchPartnerships = async () => {
    setLoading(true)
    setError('')
    try {
      const r = await api.get('/api/admin/partnerships?size=100')
      const data = Array.isArray(r.data) ? r.data : r.data.content || []
      setPartnerships(data)
    } catch (err) {
      setError(err.response?.data?.message || t('partnerships.error.load'))
    } finally {
      setLoading(false)
    }
  }

  const handleOpenModal = (p = null) => {
    setSelected(p)
    setIsModalOpen(true)
  }
  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelected(null)
  }
  const handleSubmit = async (formData) => {
    setIsSubmitting(true)
    try {
      // memberIds is a UI-only field; the backend handles members via a
      // separate sub-resource (POST/DELETE /api/admin/partnerships/{id}/members).
      // Strip it out of the entity payload, then reconcile after save.
      const { memberIds = [], ...entityPayload } = formData

      let partnershipId
      let existingMemberIds = []
      if (selected?.id) {
        await api.put(`/api/admin/partnerships/${selected.id}`, entityPayload)
        partnershipId = selected.id
        // Existing member companies — handle both response shapes from the API.
        existingMemberIds = Array.isArray(selected.members)
          ? selected.members
              .map((m) => (m.company && m.company.id) || m.companyId || null)
              .filter((id) => id != null)
          : []
      } else {
        const res = await api.post('/api/admin/partnerships', entityPayload)
        partnershipId = res.data?.id
      }

      // Sync membership: add anything new, remove anything dropped.
      if (partnershipId != null) {
        const toAdd = memberIds.filter((id) => !existingMemberIds.includes(id))
        const toRemove = existingMemberIds.filter((id) => !memberIds.includes(id))

        for (const companyId of toAdd) {
          try {
            await api.post(`/api/admin/partnerships/${partnershipId}/members`, {
              companyId,
              memberRole: 'MEMBER',
            })
          } catch (e) {
            console.warn(`Failed to add member ${companyId}:`, e.response?.data || e.message)
          }
        }
        for (const companyId of toRemove) {
          try {
            await api.delete(`/api/admin/partnerships/${partnershipId}/members/${companyId}`)
          } catch (e) {
            console.warn(`Failed to remove member ${companyId}:`, e.response?.data || e.message)
          }
        }
      }

      await fetchPartnerships()
      handleCloseModal()
    } catch (err) {
      setError(
        err.response?.data?.description ||
        err.response?.data?.message ||
        err.response?.data?.error ||
        t('partnerships.error.save')
      )
    } finally {
      setIsSubmitting(false)
    }
  }
  const handleDelete = async (e, id) => {
    e.stopPropagation()
    if (!confirm(t('partnerships.confirmDelete'))) return
    try {
      await api.delete(`/api/admin/partnerships/${id}`)
      await fetchPartnerships()
    } catch (err) {
      setError(err.response?.data?.message || t('partnerships.error.delete'))
    }
  }

  const filtered = useMemo(() => {
    let r = partnerships
    if (status !== 'all') r = r.filter((p) => (p.status || '').toLowerCase() === status)
    if (search.trim()) {
      const s = search.toLowerCase()
      r = r.filter(
        (p) =>
          (p.agreementId || '').toLowerCase().includes(s) ||
          (p.name || '').toLowerCase().includes(s) ||
          (p.partnershipType || '').toLowerCase().includes(s)
      )
    }
    return r
  }, [partnerships, status, search])

  const counts = {
    all: partnerships.length,
    active: partnerships.filter((p) => (p.status || '').toLowerCase() === 'active').length,
    pending: partnerships.filter((p) => (p.status || '').toLowerCase() === 'pending').length,
    inactive: partnerships.filter((p) => (p.status || '').toLowerCase() === 'inactive').length,
  }

  return (
    <Layout crumb={t('partnerships.crumb')}>
      <div className="view">
        <div className="view__head">
          <div className="page-head">
            <div>
              <h1 className="page-head__h"><T k="partnerships.title" /></h1>
              <div className="page-head__sub">
                <T k="partnerships.subtitle" vars={{ count: partnerships.length }} />
              </div>
            </div>
            <div className="page-head__actions">
              <button
                className="btn"
                onClick={() =>
                  exportRowsAsCsv(
                    partnerships,
                    [
                      { key: 'id', label: t('partnerships.csv.id') },
                      { key: 'agreementId', label: t('partnerships.csv.agreementId') },
                      { key: 'type', label: t('partnerships.csv.type') },
                      { key: 'status', label: t('partnerships.csv.status') },
                      { key: 'reportFrequency', label: t('partnerships.csv.frequency') },
                      { key: 'startDate', label: t('partnerships.csv.start') },
                      { key: 'endDate', label: t('partnerships.csv.end') },
                      { label: t('partnerships.csv.members'), get: (p) => (p.members || []).map((m) => m.company?.rics || m.companyRics).filter(Boolean).join(';') },
                    ],
                    'mas-partnerships'
                  )
                }
              >
                {Icon.download({ s: 12 })} {t('partnerships.action.export')}
              </button>
              <button className="btn btn--primary" onClick={() => handleOpenModal()}>
                {Icon.upload({ s: 12 })} {t('partnerships.action.new')}
              </button>
            </div>
          </div>
        </div>

        <div className="view__body thin-scroll">
        {error && (
          <div
            className="status-msg status-msg--err"
            role="alert"
            aria-live="polite"
            style={{
              marginBottom: 14,
            }}
          >
            {error}
          </div>
        )}

        <div className="card">
          <div className="tab-bar">
            {[
              ['all', t('partnerships.tab.all'), counts.all],
              ['active', t('partnerships.tab.active'), counts.active],
              ['pending', t('partnerships.tab.pending'), counts.pending],
              ['inactive', t('partnerships.tab.inactive'), counts.inactive],
            ].map(([k, l, c]) => (
              <button key={k} className={'tab-bar__tab ' + (status === k ? 'is-active' : '')} onClick={() => setStatus(k)}>
                {l} <span className="count">{c}</span>
              </button>
            ))}
          </div>

          <div className="filter-bar">
            <div className="search-input">
              {Icon.search({ s: 12 })}
              <input
                placeholder={t('partnerships.searchPlaceholder')}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 11.5, color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
              <T k="partnerships.countOf" vars={{ shown: filtered.length, total: partnerships.length }} />
            </span>
          </div>

          <div className="tbl-wrap">
            <table className="tbl table--cards">
              <thead>
                <tr>
                  <th>{t('partnerships.table.agreementId')}</th>
                  <th>{t('partnerships.table.name')}</th>
                  <th>{t('partnerships.table.type')}</th>
                  <th>{t('partnerships.table.status')}</th>
                  <th>{t('partnerships.table.start')}</th>
                  <th>{t('partnerships.table.end')}</th>
                  <th style={{ width: 24 }}></th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      <T k="partnerships.loading" />
                    </td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <td colSpan={7} style={{ textAlign: 'center', padding: 40, color: 'var(--text-dim)' }}>
                      {partnerships.length === 0
                        ? <T k="partnerships.empty.none" />
                        : <T k="partnerships.empty.filter" />}
                    </td>
                  </tr>
                ) : (
                  filtered.map((p) => (
                    <tr key={p.id} onClick={() => handleOpenModal(p)} style={{ cursor: 'pointer' }}>
                      <td className="id bold" data-label={t('partnerships.table.agreementId')}>{p.agreementId}</td>
                      <td data-label={t('partnerships.table.name')}>{p.name || '—'}</td>
                      <td className="muted mono" data-label={t('partnerships.table.type')} style={{ fontSize: 11 }}>{p.partnershipType || '—'}</td>
                      <td data-label={t('partnerships.table.status')}>
                        <span
                          className={
                            'pill pill--' +
                            ({ active: 'ok', pending: 'warn', inactive: 'err' }[(p.status || '').toLowerCase()] || 'neutral')
                          }
                        >
                          <span className="pill-dot" />
                          {p.status || '—'}
                        </span>
                      </td>
                      <td className="muted mono" data-label={t('partnerships.table.start')} style={{ fontSize: 11 }}>{p.startDate || '—'}</td>
                      <td className="muted mono" data-label={t('partnerships.table.end')} style={{ fontSize: 11 }}>{p.endDate || '—'}</td>
                      <td data-label={t('partnerships.table.actions')}>
                        <button className="iconbtn" style={{ width: 24, height: 24 }} onClick={(e) => handleDelete(e, p.id)}>
                          {Icon.close({ s: 13 })}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        </div>

        <Modal
          isOpen={isModalOpen}
          title={selected ? t('partnerships.modal.edit') : t('partnerships.modal.create')}
          onClose={handleCloseModal}
          size="lg"
        >
          <PartnershipForm partnership={selected} onSubmit={handleSubmit} isLoading={isSubmitting} />
        </Modal>
      </div>
    </Layout>
  )
}
